import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, map, of, switchMap } from 'rxjs';

import { OrdinalOrUserOrdinalUpdate } from '@ninety/platform/models/_shared/ordinal-or-user-ordinal-update';
import { SortDirection } from '@ninety/platform/models/enums/sort-direction';
import { IntervalCode } from '@ninety/platform/models/issues/interval-code';
import { extractValueFromStore } from '@ninety/platform/services';
import { RocksIssuesEvents, appActions, NotificationActions, selectLanguage } from '@ninety/platform/state';

import { GetIssuesParams, IssuesV2Service } from '../_shared/services/issues-v2.service';

import { IssuesActions } from './issues.actions';
import * as IssuesSelectors from './issues.selectors';

@Injectable()
export class IssuesRocksEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly issuesV2Service = inject(IssuesV2Service);

  loadPlanningBoardIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.getPlanningBoardIssues),
      switchMap(({ issueOptions }) => {
        const params: GetIssuesParams = {
          teamId: issueOptions.teamId,
          intervalCode: IntervalCode.longTerm,
          archived: false,
          page: 0,
          pageSize: 100,
          onlyPublic: false,
          sortField: 'None',
          sortDirection: SortDirection.ASC,
        };

        return this.issuesV2Service.getTeamIssues(params).pipe(
          map(({ items }) => {
            // Apply default sorting for planning board: rating (desc) then planningBoardOrdinal (asc
            // This feature was requested by users and we don't have server support for this yet (2 sort fields)
            const sortedItems = [...items]
              .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
              .sort((a, b) => (a.planningBoardOrdinal ?? 0) - (b.planningBoardOrdinal ?? 0));

            return IssuesActions.loadPlanningBoardIssuesSuccess({ issues: sortedItems, teamId: params.teamId });
          }),
          catchError((error: unknown) => of(IssuesActions.loadPlanningBoardIssuesFailure({ error })))
        );
      })
    )
  );

  //Rocks state will keep track of sorting direction until we figure out a better approach
  //and remove the old implementation, it uses a component store not global state
  sortIssuesLocalAndUpdateOrdinals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.sortIssuesLocalAndUpdateOrdinals),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectAllIssues),
        this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
      ]),
      switchMap(([{ sortField, sortDirection }, issues, meetingChannelId, isPresenter]) => {
        if (!issues || issues.length === 0) {
          return EMPTY;
        }

        const isAttendeeInMeeting = !!meetingChannelId && !isPresenter;
        if (isAttendeeInMeeting) {
          return of(RocksIssuesEvents.sortIssuesLocalFromBroadcast({ sortField, sortDirection }));
        }

        const ordinalUpdates: OrdinalOrUserOrdinalUpdate[] = issues.map(issue => {
          return new OrdinalOrUserOrdinalUpdate(issue._id, issue.planningBoardOrdinal ?? 0, 'planningBoardOrdinal');
        });

        return (
          this.issuesV2Service
            //teamId not needed in this case, server knows how to handle it
            //Ordinal endpoint only needs ordinals for this specific case
            .updateOrdinals(ordinalUpdates, null, IntervalCode.longTerm, 'None', SortDirection.ASC)
            .pipe(
              map(() => RocksIssuesEvents.sortIssuesLocalAndUpdateOrdinalsSuccess({ sortField, sortDirection })),
              catchError((error: unknown) =>
                of(RocksIssuesEvents.sortIssuesLocalAndUpdateOrdinalsError({ error, sortField, sortDirection }))
              )
            )
        );
      })
    )
  );

  updateOrdinalsOnDragDrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.updateOrdinalsOnDragDrop),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectAllIssues)]),
      switchMap(([{ previousIndex, currentIndex }, allIssues]) => {
        if (!allIssues || allIssues.length === 0) {
          return of(appActions.noop());
        }

        // For planning board, there's only one column, so use indices directly
        const startIndex = Math.min(previousIndex, currentIndex);
        const endIndex = Math.max(previousIndex, currentIndex);
        const issuesToUpdate = allIssues.slice(startIndex, endIndex + 1);

        // Create ordinal updates using planningBoardOrdinal
        const ordinalUpdates = issuesToUpdate.map((issue, i) => {
          return new OrdinalOrUserOrdinalUpdate(issue._id, startIndex + i, 'planningBoardOrdinal');
        });

        return this.issuesV2Service
          .updateOrdinals(ordinalUpdates, null, IntervalCode.longTerm, 'None', SortDirection.ASC)
          .pipe(
            map(() => RocksIssuesEvents.updateOrdinalsOnDragDropSuccess()),
            catchError((error: unknown) => of(RocksIssuesEvents.updateOrdinalsOnDragDropError({ error })))
          );
      })
    )
  );

  completeIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.completeIssue),
      map(({ issueId }) => IssuesActions.setCompletedState({ issueId, completed: true }))
    )
  );

  completeAndArchiveIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.completeAndArchiveIssue),
      map(({ issueId }) => IssuesActions.completeAndArchiveIssue({ issueId }))
    )
  );

  deleteIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.deleteIssue),
      map(({ issueId }) => IssuesActions.deleteIssue({ issueId }))
    )
  );

  deleteIssueFromPB$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.deleteIssueFromPB),
      map(({ issueId }) => IssuesActions.deleteIssueFromPB({ issueId }))
    )
  );

  archiveIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.archiveIssue),
      map(({ issueId }) => IssuesActions.archiveIssue({ issueId }))
    )
  );

  openIssueInDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.openIssueInDetailView),
      map(({ issue }) => IssuesActions.openInDetailViewRequested({ issue }))
    )
  );

  createIssuesFromRocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.createIssuesFromRocks),
      switchMap(({ rockIds }) =>
        this.issuesV2Service.createIssuesFromRocks(rockIds).pipe(
          map(() => {
            const language = extractValueFromStore(this.store, selectLanguage);
            return NotificationActions.notify({
              message: `${language.rock.items} successfully cloned to ${language.issue.items}.`,
            });
          }),
          catchError((error: unknown) => {
            const language = extractValueFromStore(this.store, selectLanguage);
            return of(
              NotificationActions.notifyError({
                error,
                message: `Could not create ${language.issue.items} from ${language.rock.items}. Please try again.`,
              })
            );
          })
        )
      )
    )
  );
}
